import { useEffect, useState, useCallback, useRef } from 'react';
import { formatFileSize } from '@/mocks/driveFiles';
import type { DriveItem } from '@/mocks/driveFiles';

export interface PdfItem {
  item: DriveItem;
  src: string;
}

interface PdfViewerProps {
  pdfFiles: PdfItem[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function PdfViewer({ pdfFiles, currentIndex, onClose, onNavigate }: PdfViewerProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const objectRef = useRef<HTMLObjectElement>(null);
  const current = pdfFiles[currentIndex];

  // Detect mock mode: no src means server not connected / mock mode
  const isMockMode = !current?.src;

  // Reset loading/error on file change
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [currentIndex]);

  // Fallback timeout: if object doesn't load in 8s, show error
  useEffect(() => {
    if (isMockMode) return;
    const timer = setTimeout(() => {
      if (isLoading) setHasError(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [currentIndex, isLoading, isMockMode]);

  const handleObjectLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleObjectError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowLeft') { onNavigate(Math.max(0, currentIndex - 1)); return; }
      if (e.key === 'ArrowRight') { onNavigate(Math.min(pdfFiles.length - 1, currentIndex + 1)); return; }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [currentIndex, pdfFiles.length, onClose, onNavigate]);

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-black/85 backdrop-blur-md animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 md:px-6 py-3 bg-black/60 border-b border-white/10 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500/20 flex-shrink-0">
            <i className="ri-file-pdf-2-line text-lg text-accent-400"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{current.item.name}</p>
            <p className="text-xs text-white/50">
              {formatFileSize(current.item.size ?? 0)} &middot; {currentIndex + 1} / {pdfFiles.length}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-xs text-white/40">
            ESC 닫기 &middot; ← → 이동
          </span>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors cursor-pointer"
            title="닫기 (ESC)"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div
        className="flex-1 flex items-center justify-center relative min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Previous button */}
        {pdfFiles.length > 1 && (
          <button
            onClick={() => onNavigate(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            className="absolute left-3 md:left-6 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer backdrop-blur-sm"
            title="이전"
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
          </button>
        )}

        {/* Next button */}
        {pdfFiles.length > 1 && (
          <button
            onClick={() => onNavigate(Math.min(pdfFiles.length - 1, currentIndex + 1))}
            disabled={currentIndex === pdfFiles.length - 1}
            className="absolute right-3 md:right-6 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer backdrop-blur-sm"
            title="다음"
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
          </button>
        )}

        {/* PDF viewer area */}
        <div className="w-full h-full flex items-center justify-center p-4 md:p-8">
          <div className="relative w-full max-w-4xl h-full rounded-xl overflow-hidden bg-white">
            {/* Loading spinner */}
            {isLoading && !hasError && !isMockMode && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-background-100 z-10">
                <span className="w-10 h-10 flex items-center justify-center mb-3">
                  <i className="ri-loader-4-line text-3xl text-accent-500 animate-spin"></i>
                </span>
                <p className="text-sm text-foreground-500">PDF 불러오는 중...</p>
              </div>
            )}

            {/* Mock mode fallback */}
            {isMockMode && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-background-100 z-10">
                <span className="w-16 h-16 flex items-center justify-center mb-4 rounded-2xl bg-accent-100">
                  <i className="ri-file-pdf-2-line text-4xl text-accent-500"></i>
                </span>
                <p className="text-base font-medium text-foreground-700">PDF 미리보기</p>
                <p className="text-sm text-foreground-500 mt-1 text-center max-w-sm px-4">
                  서버에 연결되면 실제 PDF 파일을 여기서 확인할 수 있습니다
                </p>
                <p className="text-xs text-foreground-400 mt-4">{current.item.name}</p>
                <p className="text-xs text-foreground-400">{formatFileSize(current.item.size ?? 0)}</p>
              </div>
            )}

            {/* Error state */}
            {hasError && !isMockMode && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-background-100 z-10">
                <span className="w-16 h-16 flex items-center justify-center mb-4 rounded-full bg-accent-100">
                  <i className="ri-error-warning-line text-3xl text-accent-500"></i>
                </span>
                <p className="text-base font-medium text-foreground-700">미리보기를 불러올 수 없습니다</p>
                <p className="text-sm text-foreground-500 mt-1">PDF 파일을 표시하는 중 문제가 발생했습니다</p>
                <p className="text-xs text-foreground-400 mt-2">{current.item.name}</p>
              </div>
            )}

            {/* PDF object (only in server mode with actual src) */}
            {!isMockMode && current.src && (
              <object
                ref={objectRef}
                data={`${current.src}#toolbar=0&navpanes=0`}
                type="application/pdf"
                className="w-full h-full"
                onLoad={handleObjectLoad}
                onError={handleObjectError}
              >
                <div className="flex flex-col items-center justify-center h-full text-foreground-500">
                  <span className="w-16 h-16 flex items-center justify-center mb-4 rounded-2xl bg-accent-100">
                    <i className="ri-file-pdf-2-line text-4xl text-accent-500"></i>
                  </span>
                  <p className="text-base font-medium text-foreground-700">PDF 뷰어를 사용할 수 없습니다</p>
                  <p className="text-sm text-foreground-500 mt-1">브라우저가 이 PDF를 표시하지 못했습니다</p>
                </div>
              </object>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}